import { useState, useCallback, useMemo } from 'react';

// ---------------------------------------------------------------------------
// Chip derivation – how a page maps filter state to visible chips
// ---------------------------------------------------------------------------

export interface FilterChip {
  id: string;
  dataText: string;
}

export interface ChipDerivation<T> {
  /** Given current filter state, return the chips to display. */
  deriveChips: (state: T) => FilterChip[];
  /** Given a chip id, return updated filter state with that chip removed. */
  removeChip: (state: T, chipId: string) => T;
}

// ---------------------------------------------------------------------------
// Filter field configuration – declarative field definitions
// ---------------------------------------------------------------------------

export type FilterFieldType = 'multi-select' | 'chip-multi-select' | 'select' | 'text';

export interface MultiSelectOption {
  id: string;
  name: string;
  labelText: string;
}

export interface FilterFieldConfig<T = Record<string, string[]>> {
  key: keyof T & string;
  type: FilterFieldType;
  label: string;
  placeholder?: string;
  options?: MultiSelectOption[];
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// useFilterState – generic two-phase (pending → applied) filter manager
// ---------------------------------------------------------------------------

export interface UseFilterStateReturn<T> {
  pendingFilters: T;
  appliedFilters: T;
  setPendingFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  chips: FilterChip[];
  filterCount: number;
  removeChip: (chipId: string) => void;
  hasChanges: boolean;
}

export function useFilterState<T>(
  defaultState: T,
  chipDerivation: ChipDerivation<T>,
  initialApplied?: T
): UseFilterStateReturn<T> {
  const [pendingFilters, setPendingFilters] = useState<T>(initialApplied ?? defaultState);
  const [appliedFilters, setAppliedFilters] = useState<T>(initialApplied ?? defaultState);

  const setPendingFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(() => {
    setPendingFilters((current) => {
      setAppliedFilters(current);
      return current;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setPendingFilters(defaultState);
    setAppliedFilters(defaultState);
  }, [defaultState]);

  const chips = useMemo(
    () => chipDerivation.deriveChips(appliedFilters),
    [chipDerivation, appliedFilters]
  );

  const filterCount = chips.length;

  const removeChip = useCallback(
    (chipId: string) => {
      // Remove from pending (chips are shown from pending in CC); sync applied to match
      setPendingFilters((prev) => {
        const next = chipDerivation.removeChip(prev, chipId);
        setAppliedFilters(next);
        return next;
      });
    },
    [chipDerivation]
  );

  const hasChanges = useMemo(
    () => JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters),
    [pendingFilters, appliedFilters]
  );

  return {
    pendingFilters,
    appliedFilters,
    setPendingFilter,
    applyFilters,
    resetFilters,
    chips,
    filterCount,
    removeChip,
    hasChanges,
  };
}
